import type { z } from "zod";
import { API_PREFIX, HEADER_CLIENT_BUILD, HEADER_CSRF, HEADER_DATA_VERSION } from "@charter/shared";
import { CLIENT_BUILD_ID } from "@/config/build";
import { API_URL } from "@/config/env";
import { gameData } from "@/data/gameData";
import { storage } from "@/platform/storage";
import { applyGateFromError } from "./appGate";

const TOKEN_KEY = "charter.auth.token";

/**
 * Guest identity: a random token minted on first launch and kept in storage. The server maps it to a
 * player id. Replace with a real identity provider by changing this function and server/auth.ts.
 */
export async function getAuthToken(): Promise<string> {
  const existing = await storage.get(TOKEN_KEY);
  if (existing) return existing;
  const token = `guest:${crypto.randomUUID()}`;
  await storage.set(TOKEN_KEY, token);
  return token;
}

class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export interface ApiResponse<T> {
  status: number;
  data: T;
}

/**
 * Typed fetch: adds auth + build headers, parses JSON, validates with the given schema.
 * Throws ApiRequestError for non-2xx (callers decide what a 409 or 404 means).
 */
export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  init: { method?: string; body?: unknown; accept?: number[] } = {},
): Promise<ApiResponse<T>> {
  const token = await getAuthToken();
  const res = await fetch(`${API_URL}${API_PREFIX}${path}`, {
    method: init.method ?? "GET",
    headers: {
      authorization: `Bearer ${token}`,
      "content-type": "application/json",
      [HEADER_CLIENT_BUILD]: CLIENT_BUILD_ID,
      [HEADER_DATA_VERSION]: gameData.version,
      ...(init.method && init.method !== "GET" ? { [HEADER_CSRF]: "1" } : {}),
    },
    ...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
  });
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok && !(init.accept ?? []).includes(res.status)) {
    const err = json as { error?: { code?: string; message?: string } } | null;
    applyGateFromError(res.status, err?.error?.code ?? "", err?.error?.message ?? "");
    throw new ApiRequestError(
      res.status,
      err?.error?.code ?? "http_error",
      err?.error?.message ?? res.statusText,
      json,
    );
  }
  return { status: res.status, data: schema.parse(json) };
}
